import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/Spinner";
import { toast } from "sonner";
import { Check, X, Users, MessageCircle } from "lucide-react";

export default function NewChatDialog({ open, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState(null);
  const [q, setQ] = useState("");
  const [shared, setShared] = useState(false);
  const [pickedA, setPickedA] = useState(null);
  const [pickedB, setPickedB] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPickedA(null); setPickedB(null); setShared(false); setQ(""); setCoaches(null);
    api.get("/coaches").then((r) => setCoaches(Array.isArray(r.data) ? r.data : [])).catch(() => setCoaches([]));
  }, [open]);

  const filtered = useMemo(() => {
    const list = coaches || [];
    if (!q.trim()) return list;
    const s = q.trim().toLowerCase();
    return list.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const methods = ((c.profile?.methods || []).join(" ")).toLowerCase();
      return name.includes(s) || methods.includes(s);
    });
  }, [coaches, q]);

  const pickingSlot = pickedA && shared && !pickedB ? "b" : "a";
  const activePick = pickingSlot === "b" ? pickedB : pickedA;

  const pick = (c) => {
    if (pickingSlot === "a") { setPickedA(c); return; }
    if (c.id === pickedA?.id) { toast.error(t("new_chat_error_same_coach")); return; }
    setPickedB(c);
  };

  const start = async () => {
    if (!pickedA || !user?.id) return;
    setBusy(true);
    try {
      if (shared && pickedB) {
        const { data } = await api.post("/collaboration/shared-chat/client-initiate", { coach_a_id: pickedA.id, coach_b_id: pickedB.id, client_id: user.id });
        onClose();
        navigate(`/app/shared-chat/${data.id}`);
      } else {
        onClose();
        navigate(`/app/messages/${pickedA.id}`, { state: { name: pickedA.name, photo: pickedA.profile?.photo } });
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const canStart = !!pickedA && (!shared || !!pickedB);

  const Chip = ({ label, coach, onClear, active }) => (
    <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${active ? "border-primary bg-primary/5" : "border-border bg-card"} ${!coach ? "min-w-[140px] italic text-muted-foreground" : ""}`}>
      {coach ? (
        <>
          {coach.profile?.photo ? <img src={coach.profile.photo} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">{(coach.name || "?")[0]}</span>}
          <span className="max-w-[120px] truncate">{coach.name}</span>
          <button onClick={onClear}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>
        </>
      ) : label}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-chat-modal">
        <DialogHeader><DialogTitle>{t("new_chat_title")}</DialogTitle></DialogHeader>

        <div className="flex flex-wrap gap-2">
          <Chip label={`${t("new_chat_pick_coach")} · A`} coach={pickedA} onClear={() => { setPickedA(null); setPickedB(null); }} active={pickingSlot === "a"} />
          {shared ? <Chip label={`${t("new_chat_pick_second")} · B`} coach={pickedB} onClear={() => setPickedB(null)} active={pickingSlot === "b"} /> : null}
        </div>

        <button onClick={() => { setShared((v) => !v); if (shared) setPickedB(null); }} data-testid="new-chat-shared-toggle" className="flex items-center gap-2.5 py-1 text-left text-sm text-foreground">
          <span className={`flex h-5 w-5 items-center justify-center rounded-md border-2 ${shared ? "border-primary bg-primary" : "border-border"}`}>{shared ? <Check className="h-3 w-3 text-primary-foreground" /> : null}</span>
          {t("new_chat_shared_toggle")}
        </button>

        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("new_chat_search_placeholder")} data-testid="new-chat-search" />

        <div className="max-h-[42vh] space-y-2 overflow-y-auto">
          {coaches === null ? <Spinner label={t("common.loading")} /> : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm italic text-muted-foreground">{t("new_chat_no_results")}</p>
          ) : filtered.map((c) => {
            const picked = c.id === activePick?.id;
            return (
              <button key={c.id} onClick={() => pick(c)} data-testid={`new-chat-pick-${c.id}`}
                className={`flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition-colors ${picked ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}>
                {c.profile?.photo ? <img src={c.profile.photo} alt="" className="h-9 w-9 rounded-full object-cover" /> : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">{(c.name || "?")[0]}</span>}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-foreground">{c.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{c.profile?.headline || (c.profile?.methods || []).slice(0, 2).join(", ")}</span>
                </span>
                {picked ? <Check className="h-4 w-4 text-primary" /> : null}
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="new-chat-cancel">{t("new_chat_cancel")}</Button>
          <Button onClick={start} disabled={!canStart || busy} className="rounded-full" data-testid="new-chat-start">
            {shared ? <Users className="mr-1.5 h-4 w-4" /> : <MessageCircle className="mr-1.5 h-4 w-4" />}
            {shared ? t("new_chat_start_shared_btn") : t("new_chat_start_btn")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
